import EventBusyIcon from "@mui/icons-material/EventBusy";
import { Box, Skeleton } from "@mui/material";
import { DateTime } from "luxon";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "../../../components/UIToolkit/molecules/EmptyState";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useWeekIndex } from "../../../contexts/WeekIndexProvider";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { NEUTRAL } from "../../../theme/tokens";
import { getWeekStart } from "../../../utils/dateUtils";
import { resolveEffectiveWeekStartDay } from "../v2/resolveEffectiveWeekStartDay";
import type { CoverageEditWiring } from "./CoverageGrid";
import { CoverageSummaryPrototype } from "./CoverageSummaryPrototype";
import { useMasterScheduleCoverage } from "./hooks";
import { type CoverageSummaryResponse, CoverageTargetMode } from "./types";

/** A coverage response is empty when there is no horizon or no slot rows to show. */
const isEmptyCoverage = (data: CoverageSummaryResponse): boolean =>
	data.weeks === 0 || data.sections.length === 0 || data.sections.every((s) => s.rows.length === 0);

/** Stable keys for the fixed, non-reordering skeleton rows. */
const SKELETON_ROW_KEYS = ["a", "b", "c", "d", "e", "f", "g", "h"] as const;

/** Lightweight grid skeleton — a few shimmer rows that read as "a grid is loading". */
const CoverageGridSkeleton = () => (
	<Box
		data-testid="ms3-coverage-loading"
		sx={{ display: "flex", flexDirection: "column", gap: 1, height: "100%", border: `1px solid ${NEUTRAL[200]}`, borderRadius: 2, p: 1.5, overflow: "hidden" }}
	>
		{SKELETON_ROW_KEYS.map((key) => (
			<Skeleton key={`ms3-coverage-skeleton-${key}`} variant="rounded" height={28} sx={{ borderRadius: 1, flexShrink: 0 }} />
		))}
	</Box>
);

/**
 * Production Coverage view. Wires the live coverage-summary endpoint into `CoverageSummaryPrototype`,
 * which owns the chrome + quadrants. Calculations stay backend-only — the grid renders the BE's numbers
 * verbatim; `computeSectionRollup` is presentation aggregation only. The People axis has moved to the
 * Staffing area (see `StaffingPeopleView`).
 */
export const MasterScheduleCoverage = ({ trailing }: { trailing?: ReactNode }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { chosenDepartment, timezone } = useHospitalData();
	const isRuntimeWeekStartEnabled = useFeatureFlag(FEATURE_FLAGS.ScheduleTemplateRuntimeWeekStart);
	const { viewStartDay } = useWeekIndex();
	// Current-cycle anchor used while a response is still loading (the grid isn't rendered then —
	// fallbackNode shows — but the child needs a concrete cycleStart). Matches the hook's anchor.
	const cycleStartAnchor = useMemo(
		() => getWeekStart(DateTime.now().setZone(timezone), resolveEffectiveWeekStartDay(isRuntimeWeekStartEnabled, viewStartDay)),
		[timezone, isRuntimeWeekStartEnabled, viewStartDay],
	);
	// Coverage target mode (By census / By max). Default census; a no-ladder dept downgrades to max
	// server-side and reports it back via `data.targetMode` (which drives the toggle's active value).
	const [targetMode, setTargetMode] = useState<CoverageTargetMode>(CoverageTargetMode.Census);
	const { data, isLoading, isError, refetch } = useMasterScheduleCoverage(targetMode);

	// Location-config editing wiring for the Summary grid's Location cells (mirrors v2's editor
	// inputs: week-start day, section names, save-time refetch, and the edit-blocked gate).
	const editWiring = useMemo<CoverageEditWiring>(() => {
		const uniqueSections = Array.from(new Set((chosenDepartment?.locations ?? []).map((loc) => loc.section).filter((s): s is string => !!s))).sort((a, b) =>
			a.localeCompare(b),
		);
		return {
			weekStartDay: resolveEffectiveWeekStartDay(isRuntimeWeekStartEnabled, viewStartDay),
			uniqueSections,
			onCoverageRefresh: refetch,
			editBlocked: !!chosenDepartment?.disable_edit_master_schedule,
			editBlockedTooltip: t("schedule.template.edit.blocked"),
		};
	}, [chosenDepartment?.locations, chosenDepartment?.disable_edit_master_schedule, isRuntimeWeekStartEnabled, viewStartDay, refetch, t]);

	useEffect(() => {
		track("schedule_template_coverage_summary_view", { department_id: chosenDepartment?.department_id });
	}, [track, chosenDepartment?.department_id]);

	// Non-data state (loading / error / empty) to render in place of the grid, or null when ready.
	// Shared by the Coverage Summary + Detail quadrants (same hook, one status surface).
	const fallbackNode = useMemo((): ReactNode | null => {
		if (isLoading) return <CoverageGridSkeleton />;
		if (isError || !data) {
			return (
				<EmptyState
					dataTestId="ms3-coverage-error"
					icon={<EventBusyIcon />}
					title={t("schedule.template.v3.coverage.error.title")}
					hint={t("schedule.template.v3.coverage.error.hint")}
					action={
						<SystemButton buttonVariant="secondary" size="small" onClick={() => refetch()} data-testid="ms3-coverage-refresh">
							{t("schedule.template.v3.coverage.error.retry")}
						</SystemButton>
					}
				/>
			);
		}
		if (isEmptyCoverage(data)) {
			return (
				<EmptyState
					dataTestId="ms3-coverage-empty"
					icon={<EventBusyIcon />}
					title={t("schedule.template.v3.coverage.empty.title")}
					hint={t("schedule.template.v3.coverage.empty.hint")}
				/>
			);
		}
		return null;
	}, [isLoading, isError, data, refetch, t]);

	return (
		<CoverageSummaryPrototype
			liveCoverage={{
				fallbackNode,
				sections: data?.sections ?? [],
				weeks: data?.weeks ?? 0,
				cycleStart: data ? DateTime.fromISO(data.cycleStart) : cycleStartAnchor,
				editWiring,
				// Active value follows the RESOLVED mode (a no-ladder dept comes back as max); the
				// requested state only drives the fetch. hasRatioLadder picks the toggle pair —
				// undefined until the first response so the switch waits instead of flashing labels.
				targetMode: data?.targetMode ?? targetMode,
				onTargetModeChange: setTargetMode,
				hasRatioLadder: data?.hasRatioLadder,
			}}
			trailing={trailing}
		/>
	);
};
